import type * as React from 'react';
import { useState } from 'react';
import { ImageIcon, ImageUpIcon, Loader2 } from 'lucide-react';

import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { recognizeInsightsScreen, type RecognizedScreen } from '../api/insights-screen';
import { downscaleToDataUrl } from '../lib/downscale';
import { toScreenImage, type ScreenImage } from '../lib/render/insights-canvas';
import { ImageField } from './image-field';

/**
 * Скриншот строки табличного отчёта — акта об услугах и «посева».
 *
 * У строк с цифрами экран статистики нарисован по данным Instagram, у
 * остальных его неоткуда взять — там скриншот приносит человек. И то и
 * другое живёт в одной ячейке: кнопка показывает, что уже стоит, а
 * окно даёт заменить, вставить из буфера или убрать.
 *
 * Принесённый скриншот заодно читает модель — та же, что в презентации: если
 * паблик не подключён, цифры в строке взять больше неоткуда, и набирать их
 * руками по каждой публикации и есть та работа, ради которой отчёт
 * автоматизировали. Сам скриншот при этом остаётся картинкой в документе: его
 * для этого и принесли. Разбор не удался — строка просто заполняется руками.
 */
export function ScreenCell({
  screen,
  title,
  fieldId,
  onChange,
  onRecognized,
  disabled,
}: {
  screen: ScreenImage | null;
  /** Чей это экран — в заголовке окна: «@kazpress». */
  title: string;
  /**
   * Свой ключ строки. Именно он, а не имя паблика: под одним пабликом бывает
   * несколько строк, и по имени они перебивали друг друга в реестре вставки.
   */
  fieldId: string;
  onChange: (screen: ScreenImage | null) => void;
  /** Цифры, снятые со скриншота. Не задан — скриншот просто картинка. */
  onRecognized?: (data: RecognizedScreen) => void;
  disabled: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [isReading, setIsReading] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={screen ? `Экран: ${title}` : `Добавить скриншот: ${title}`}
        title={screen ? 'Экран статистики — открыть' : 'Скриншота нет — добавить'}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {/*
          Иконка, а не превью: экран статистики — тёмный прямоугольник, и
          в двадцать пикселей он читается как чёрное пятно. Что именно на
          нём, показывает окно.
        */}
        {screen ? <ImageIcon /> : <ImageUpIcon className="text-muted-foreground/60" />}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Скриншот: {title || 'строка без паблика'}</DialogTitle>
            <DialogDescription>
              Экран статистики рисуется сам по цифрам Instagram. Если цифр нет — принесите
              скриншот из приложения: перетащите файл или вставьте из буфера.
            </DialogDescription>
          </DialogHeader>

          <ImageField
            id={`screen-cell-${fieldId}`}
            label=""
            hint={
              onRecognized
                ? 'Встанет в отчёте рядом со ссылкой, а цифры с него снимутся сами.'
                : 'Встанет в отчёте рядом со ссылкой.'
            }
            file={screen?.blob ?? null}
            fallbackName="Экран статистики"
            onChange={(file) => {
              if (!file) {
                onChange(null);
                return;
              }
              // Приносим к тому же виду, что и нарисованные экраны.
              void toScreenImage(file)
                .then(onChange)
                .catch(() => toast.error('Не удалось прочитать скриншот'));
              if (!onRecognized) return;
              setIsReading(true);
              void downscaleToDataUrl(file)
                .then(recognizeInsightsScreen)
                .then((data) => {
                  onRecognized(data);
                  toast.success('Цифры со скриншота подставлены — проверьте');
                })
                .catch((err: unknown) => {
                  console.error('[Отчёт] разбор скриншота:', err);
                  toast.error(
                    err instanceof Error ? err.message : 'Цифры со скриншота не прочитались',
                  );
                })
                .finally(() => setIsReading(false));
            }}
            disabled={disabled || isReading}
          />

          {isReading && (
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Loader2 className="size-3.5 animate-spin" />
              Читаю цифры со скриншота...
            </p>
          )}

          {!screen && !isReading && (
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <ImageIcon className="size-3.5" />
              Пока пусто — в документе на этом месте будет пустое место.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
